import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DataProvider from "../../apis";
import moment from "moment";
import { Button, Collapse, CollapseProps, Tag } from "antd";
import {
  FaChevronLeft,
  FaCalendarDays,
  FaFileMedical,
  FaUserLarge,
  FaNotesMedical,
  FaClipboardList,
  FaClosedCaptioning,
  FaCirclePlay,
} from "react-icons/fa6";
import Empty from "../../components/elements/empty";
import AppointmentCountdown from "../../components/elements/appointment-countdown";
import { checkAppointmentStatus } from "../../utils";
import { parseMeetingTranscript } from "../../utils/transcription";
import PatientInfo from "../../components/sections/patient-info";
import PatientRecords from "../../components/sections/patient-records";
import JoinMeetingButton from "../../components/elements/join-meeting-button";
import LoaderAnimate from "../../components/elements/loader-animate";
import { appointmentStatusConfig } from "../../constants/appointments";

function ConsultationTranscriptView({ rawTranscript }: { rawTranscript: string }) {
  const lines = parseMeetingTranscript(rawTranscript);

  if (lines.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50/80 p-4">
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
          {rawTranscript}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
      {lines.map((line, idx) => (
        <div
          key={idx}
          className="rounded-xl border border-slate-200/80 bg-slate-50/80 px-4 py-2.5"
        >
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs font-semibold text-primary-blue shrink-0">
              {line.speaker}
              {line.time != null && (
                <span className="ml-1.5 font-normal text-slate-400">
                  {line.time}
                </span>
              )}
            </span>
          </div>
          <p className="mt-0.5 text-sm text-slate-700 leading-relaxed">
            {line.text}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Details() {
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [appt, setAppt] = useState<any>(null);
  const [status, setStatus] = useState<boolean>(true);
  const [count, setCount] = useState<boolean>(false);
  const [patient, setPatient] = useState<any>(null);
  const [records, setRecords] = useState<any>(null);
  const [targetDate, setTargetDate] = useState({
    date: "",
    startTime: "",
    endTime: "",
  });
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);

  const onFetch = async (id: string | undefined) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await DataProvider.getSingleAppointment(id);
      setAppt(data);
      const state = checkAppointmentStatus(
        data.date,
        data.startTime,
        data.endTime
      );

      if (state === "passed") {
        setStatus(true);
      } else if (state === "within-time") {
        setStatus(false);
      } else if (state === "waiting") {
        setTargetDate({
          date: data.date,
          startTime: data.startTime,
          endTime: data.endTime,
        });
        setCount(true);
      }

      if (data?.user) {
        setPatient(data.user);
        const res = await DataProvider.getUserReports(data.user.id);
        setRecords(res.data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onFetch(params.id);
  }, [params.id]);

  function onCancel() {
    if (appt?.id) navigate(`/appointments/cancel/${appt.id}`);
  }

  function onReschedule() {
    if (appt?.id) navigate(`/appointments/reschedule/${appt.id}`);
  }

  async function onGenerateTranscription() {
    if (!appt?.id) return;
    setTranscriptionError(null);
    setTranscriptionLoading(true);
    try {
      const { data: updated } = await DataProvider.generateTranscription(String(appt.id));
      setAppt((prev: any) => (prev && updated ? { ...prev, ...updated, meetingTranscript: updated.meetingTranscript ?? updated.meeting_transcript } : prev));
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Could not generate transcription.";
      setTranscriptionError(typeof msg === "string" ? msg : msg?.[0] ?? "Could not generate transcription.");
    } finally {
      setTranscriptionLoading(false);
    }
  }

  const patientCollapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: (
        <span className="flex items-center gap-2 font-semibold text-slate-800">
          <FaUserLarge className="text-primary-blue shrink-0" aria-hidden />
          Patient information
        </span>
      ),
      children: patient ? <PatientInfo data={patient} /> : null,
    },
  ];

  const medicalRecordsCollapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: (
        <span className="flex items-center gap-2 font-semibold text-slate-800">
          <FaClipboardList className="text-primary-blue shrink-0" aria-hidden />
          Patient medical records
        </span>
      ),
      children: <PatientRecords data={records} />,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center rounded-2xl border border-slate-200/80 bg-white shadow-card">
        <LoaderAnimate />
      </div>
    );
  }

  if (!appt) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center rounded-2xl border border-slate-200/80 bg-white shadow-card p-6">
        <Empty
          imageStyle="md:h-44 h-24"
          titleStyle="md:text-lg text-sm"
          desStyle="md:text-base"
          title="Appointment not found"
          buttons={[
            { name: "Refresh", event: () => onFetch(params.id) },
            { name: "Go back", event: () => navigate(-1) },
          ]}
          description="We couldn't load this appointment."
        />
      </div>
    );
  }

  const formattedDate = moment(appt.date, "DD-MM-YYYY").format("Do MMMM, YYYY");
  const timeRange = `${appt.startTime} – ${appt.endTime}`;

  return (
    <div className="min-h-full bg-medpharma-mist/40">
      {/* Page header */}
      <header className="mb-6">
        <Button
          type="text"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:!text-primary-blue hover:!bg-primary-blue/5 -ml-2 mb-3"
          aria-label="Go back to appointments"
        >
          <FaChevronLeft aria-hidden />
          <span>Back</span>
        </Button>
        <div className="flex items-center gap-2.5">
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight">
            Appointment details
          </h1>
          <Tag
            color={appointmentStatusConfig[appt.status]?.color ?? "default"}
            className="rounded text-xs"
          >
            {appointmentStatusConfig[appt.status]?.label ?? appt.status}
          </Tag>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          {formattedDate} · {timeRange}
        </p>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left: actions & schedule & report */}
        <div className="lg:col-span-2 space-y-4">
          {count && (
            <section
              className="rounded-2xl border border-primary-blue/20 bg-gradient-to-br from-primary-blue/5 to-white p-5 shadow-card"
              aria-label="Time until appointment"
            >
              <AppointmentCountdown
                setStatus={setStatus}
                date={targetDate.date}
                startTime={targetDate.startTime}
                endTime={targetDate.endTime}
                onCountdownEnd={() => onFetch(params.id)}
                params={params.id}
              />
            </section>
          )}

          <section
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
            aria-label="Appointment actions"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Actions
            </h2>
            <div className="flex flex-col gap-3 [&_button]:w-full [&_button]:justify-center">
              <JoinMeetingButton appt={appt} status={status} />
              <Button
                disabled={status}
                onClick={onReschedule}
                size="large"
                className="!border-primary-blue !text-primary-blue hover:!bg-primary-blue/5 w-full justify-center font-medium"
              >
                Reschedule
              </Button>
              <Button
                disabled={status}
                danger
                type="text"
                onClick={onCancel}
                size="large"
                className="w-full font-medium"
              >
                Cancel appointment
              </Button>
            </div>
          </section>

          <section
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
            aria-label="Schedule"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-blue/10 text-primary-blue">
                <FaCalendarDays className="text-lg" aria-hidden />
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Schedule
                </h3>
                <p className="mt-1 font-semibold text-slate-800">
                  {formattedDate}
                </p>
                <p className="text-sm text-slate-600">{timeRange}</p>
              </div>
            </div>
          </section>

          <section
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
            aria-label="Doctor report"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                <FaFileMedical className="text-lg" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-800">Doctor report</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  {appt?.doctor_report
                    ? "View the consultation report"
                    : "Create the consultation report"}
                </p>
                <Button
                  type="primary"
                  size="middle"
                  className="mt-3 !bg-primary-blue hover:!bg-primary-blue/90"
                  onClick={() => navigate(`/appointments/${appt.id}/report`)}
                >
                  {appt?.doctor_report ? "View report" : "Create report"}
                </Button>
              </div>
            </div>
          </section>
        </div>

        {/* Right: patient, records, health info, notes */}
        <div className="lg:col-span-3 space-y-4">
          {patient && (
            <section
              className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card"
              aria-label="Patient information"
            >
              <Collapse
                expandIconPosition="end"
                items={patientCollapseItems}
                className="appointment-detail-collapse"
              />
            </section>
          )}

          <section
            className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card"
            aria-label="Patient medical records"
          >
            <Collapse
              expandIconPosition="end"
              items={medicalRecordsCollapseItems}
              className="appointment-detail-collapse"
            />
          </section>

          <section
            className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-card"
            aria-label="Consultation transcription"
          >
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
              <FaClosedCaptioning className="text-primary-blue shrink-0" aria-hidden />
              <h2 className="font-semibold text-slate-800">
                Consultation transcription
              </h2>
            </div>
            <div className="p-5">
              {appt.meetingTranscript?.trim() ? (
                <ConsultationTranscriptView rawTranscript={appt.meetingTranscript} />
              ) : (
                <div className="rounded-xl border border-slate-200/80 bg-slate-50/30 p-5 text-center">
                  <p className="text-sm text-slate-600 mb-4">
                    Generate a written summary from the consultation meeting recording.
                  </p>
                  {transcriptionError && (
                    <p className="text-sm text-red-600 mb-3" role="alert">
                      {transcriptionError}
                    </p>
                  )}
                  <Button
                    type="primary"
                    size="middle"
                    loading={transcriptionLoading}
                    onClick={onGenerateTranscription}
                    className="!bg-primary-blue hover:!bg-primary-blue/90 font-medium"
                    icon={<FaCirclePlay className="mr-2" aria-hidden />}
                  >
                    {transcriptionLoading ? "Generating…" : "Generate transcription"}
                  </Button>
                </div>
              )}
            </div>
          </section>

          <section
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
            aria-label="Health information"
          >
            <div className="flex items-center gap-2 mb-4">
              <FaNotesMedical className="text-primary-blue shrink-0" aria-hidden />
              <h2 className="font-semibold text-slate-800">Health information</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="rounded-xl bg-slate-50/80 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Underlying disease
                </h4>
                <p className="mt-1 font-medium text-slate-800">
                  {appt.hasUnderlyingDisease ? "Yes" : "No"}
                </p>
                {appt.hasUnderlyingDisease && appt.underlyingDiseaseNotes && (
                  <p className="mt-1 text-sm text-slate-600">
                    {appt.underlyingDiseaseNotes}
                  </p>
                )}
              </div>
              <div className="rounded-xl bg-slate-50/80 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Previous medication
                </h4>
                <p className="mt-1 font-medium text-slate-800">
                  {appt.takesPreviousMedication ? "Yes" : "No"}
                </p>
                {appt.takesPreviousMedication && (
                  <p className="mt-1 text-sm text-slate-600">
                    {appt.upreviousMedicationNotes ?? "—"}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section
            className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card"
            aria-label="Notes"
          >
            <h2 className="font-semibold text-slate-800 mb-3">Notes</h2>
            <div className="rounded-xl bg-slate-50/80 p-4 min-h-[100px]">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">
                {appt.complaint?.trim() ? appt.complaint : "—"}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
